/**
 * 商談ステージのパイプライン表示 (2026-07-29)。
 *
 * 旧 UI はステージがバッジ 1 個（「個別説明」）で、商談が全工程のどこにいるかが
 * 読み取れなかった。資料請求 → 納品 までの通過点を 1 本のステッパーで見せる。
 *
 * - 表示専用。操作は従来どおり（前後移動 = 概要タブ / 成約・失注 = 見積タブ）。
 *   誤タップでステージが動く事故を避けるための意図的な選択。
 * - 同意ステップ (agreed) は主軸カテゴリーの requiresConsent が ON のときだけ挟む。
 *   ON でも同じ主軸カテゴリーの別の商談でサイン済みの顧客は挟まない (useDealConsentRequirement で判定)。
 * - 失注 (lost) はステッパーの代わりに理由付きバナーを出す。
 */
import type { DealStage } from '@goenhub/shared';
import { Check, XCircle } from 'lucide-react';

import type { DealRecord } from '@/hooks/useDeals';

const STAGE_LABEL: Record<DealStage, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '納品',
  lost: '失注',
};

export function DealStageStepper({
  deal,
  requiresConsent,
}: {
  deal: DealRecord;
  requiresConsent: boolean;
}) {
  if (deal.stage === 'lost') {
    return (
      <div className="mb-4 rounded-md border border-[var(--accent-red)] bg-[var(--accent-red-subtle)] px-4 py-2.5 flex items-center gap-2">
        <XCircle className="size-4 text-[var(--accent-red)] flex-shrink-0" />
        <span className="text-sm font-semibold text-[var(--accent-red)]">失注</span>
        {deal.lostReason && (
          <span className="text-xs text-[var(--text-secondary)]">理由: {deal.lostReason}</span>
        )}
      </div>
    );
  }

  const flow: DealStage[] = [
    'document_requested',
    'briefing',
    'quote',
    'won',
    // agreed 中の商談は設定 OFF でも現在地を出せるように挟む
    ...(requiresConsent || deal.stage === 'agreed' ? (['agreed'] as DealStage[]) : []),
    'paid',
    'ordered',
    'delivered',
  ];
  const currentIdx = flow.indexOf(deal.stage);

  return (
    <div className="mb-4 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-4 py-3 overflow-x-auto">
      <div className="flex items-center min-w-max">
        {flow.map((stage, i) => {
          const done = currentIdx >= 0 && i < currentIdx;
          const current = i === currentIdx;
          return (
            <div key={stage} className="flex items-center">
              {i > 0 && (
                <div
                  className={`h-px w-5 sm:w-8 mx-1 ${
                    done || current ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'
                  }`}
                />
              )}
              <div
                className="flex items-center gap-1.5"
                title={`${STAGE_LABEL[stage]}${current ? '（現在）' : done ? '（通過済）' : ''}`}
              >
                <span
                  className={
                    'flex items-center justify-center rounded-full flex-shrink-0 ' +
                    (current
                      ? 'size-5 bg-[var(--accent)] text-white ring-2 ring-[var(--accent-subtle)]'
                      : done
                        ? 'size-4 bg-[var(--accent)] text-white'
                        : 'size-4 border border-[var(--border-strong,var(--border))] bg-[var(--bg-elevated)]')
                  }
                >
                  {done && <Check className="size-2.5" />}
                  {current && <span className="size-1.5 rounded-full bg-white" />}
                </span>
                <span
                  className={
                    'text-[11px] whitespace-nowrap ' +
                    (current
                      ? 'font-bold text-[var(--accent)]'
                      : done
                        ? 'font-medium text-[var(--text-secondary)]'
                        : 'text-[var(--text-muted)]')
                  }
                >
                  {STAGE_LABEL[stage]}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
